<template>
    <skeleton v-if="isLoading" />
    <form
        v-else
        @submit="onSubmit"
    >
        <content-reset :body="formMarkup" />

        <div
            v-if="activeFacilityCount > 1"
            class="mt-6 flex flex-col"
        >
            <label
                for="select_facility"
                class="font-semibold"
            >
                {{ facilityLabel }}
            </label>
            <span class="w-full">
                <facility-select
                    id="select_facility"
                    v-model="facilityId"
                    :facility-groups="facilityGroups"
                    :invalid="errors.facilityId !== undefined"
                />
            </span>
            <span
                id="facility-error"
                class="text-small text-red-600"
            >
                {{ errors.facilityId || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2 grid gap-x-4 sm:grid-cols-2">
            <div>
                <label class="font-semibold">{{
                    t('customerInformation.firstName')
                }}</label>
                <span class="w-full">
                    <input-text
                        id="first-name"
                        v-model="firstNameValue"
                        v-bind="firstNameProps"
                        class="w-full"
                        :placeholder="t('customerInformation.firstName')"
                        maxlength="255"
                        aria-describedby="first-name-error"
                        autofocus
                    />
                </span>
                <span
                    id="first-name-error"
                    class="text-small text-red-600"
                >
                    {{ errors.firstName || '&nbsp;' }}
                </span>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('customerInformation.lastName')
                }}</label>
                <span class="w-full">
                    <input-text
                        id="last-name"
                        v-model="lastNameValue"
                        v-bind="lastNameProps"
                        class="w-full"
                        :placeholder="t('customerInformation.lastName')"
                        maxlength="255"
                        aria-describedby="last-name-error"
                    />
                </span>
                <span
                    id="last-name-error"
                    class="text-small text-red-600"
                >
                    {{ errors.lastName || '&nbsp;' }}
                </span>
            </div>
        </div>

        <div>
            <label class="font-semibold">{{
                t('customerInformation.emailAddress')
            }}</label>
            <span class="p-input-icon-left w-full">
                <i class="pi pi-envelope"></i>
                <input-text
                    id="email"
                    v-model="emailValue"
                    v-bind="emailProps"
                    inputmode="email"
                    type="email"
                    class="w-full !text-[16px]"
                    :class="{ 'p-invalid': errors.email }"
                    :placeholder="t('customerInformation.emailAddress')"
                    aria-describedby="email-error"
                />
            </span>
            <span
                id="email-error"
                class="text-small text-red-600"
                >{{ errors.email || '&nbsp;' }}</span
            >
        </div>

        <div class="mt-1">
            <label class="font-semibold">{{ t('subject') }}</label>
            <span class="w-full">
                <input-text
                    id="subject"
                    v-model="subjectValue"
                    v-bind="subjectProps"
                    class="w-full"
                    :placeholder="t('subject')"
                    maxlength="255"
                    aria-describedby="subject-error"
                />
            </span>
            <span
                id="subject-error"
                class="text-small text-red-600"
            >
                {{ errors.subject || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-1">
            <label class="font-semibold">{{ t('message') }}</label>
            <span class="w-full">
                <prime-textarea
                    id="message"
                    v-model="messageValue"
                    v-bind="messageProps"
                    class="w-full"
                    :placeholder="t('message')"
                    :rows="5"
                    :cols="30"
                    maxlength="1000"
                    aria-describedby="message-error"
                />
            </span>
            <span
                id="message-error"
                class="text-small text-red-600"
            >
                {{ errors.message || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2">
            <rp-recaptcha @token="handleRecaptchaToken" />
            <span class="text-small text-red-600">
                {{ errors.recaptchaToken || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2 flex justify-end">
            <prime-button
                type="submit"
                :label="t('submit')"
                :loading="isPending"
            />
        </div>
    </form>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import PrimeTextarea from 'primevue/textarea'
import RpRecaptcha from '~/components/Verification/RpRecaptcha.vue'
import Skeleton from 'primevue/skeleton'
import ContentReset from '~/components/ContentReset.vue'
import { object, string } from 'yup'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, toRef, watch } from 'vue'
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'
import { useToast } from 'primevue/usetoast'
import { useCreateAnonymousTicketMutation } from '~/components/Tickets/use-create-anonymous-ticket-mutation'
import { TicketConfirmationTicketFragment } from '~/components/Tickets/TicketConfirmation.vue'
import FacilitySelect from '~/components/Verification/FacilitySelect.vue'
import { useI18n } from 'vue-i18n'
import { graphql } from '~/resources/graphql'

const props = defineProps<{
    formMarkup: string
    // If this props is not provided, a facility selector will be shown
    facilityIdContext?: string
}>()

const emit = defineEmits<{
    (e: 'ticketCreated', payload: typeof TicketConfirmationTicketFragment): void
}>()

const { t } = useI18n({ useScope: 'global' })
const facilityIdContextRef = toRef(props, 'facilityIdContext')

const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 100 facilities
const { data, isLoading } = useQuery({
    queryKey: ['AnonymousContactUsTicketFormQuery', facilityIdContextRef],
    placeholderData: previousData => previousData,
    queryFn: () => {
        // If a facility has been provided, no need for this query
        if (facilityIdContextRef.value !== undefined) {
            return null
        }

        return query(
            graphql(/* GraphQL */ `
                query AnonymousContactUsTicketFormQuery {
                    organization {
                        facilityLocationGroups {
                            id
                            name
                            facilities {
                                id
                                label
                            }
                        }
                    }
                }
            `)
        )
    }
})

const facilityGroups = computed(() => {
    // If a facility has been provided, no need for this
    if (props.facilityIdContext !== undefined) {
        return []
    }

    return [
        ...(data.value?.organization.facilityLocationGroups ?? []).map(
            group => ({
                id: group.id,
                name: group.name,
                facilities: group.facilities
            })
        )
    ]
})

const activeFacilities = computed(() =>
    facilityGroups.value.flatMap(group => group.facilities)
)

const activeFacilityCount = computed(() => activeFacilities.value?.length ?? 0)
const facilityLabel = computed(() => t('facility', 1))

// Reuse the shared locale messages so anonymous and logged-in contact forms
// validate with the same wording.
const schema = toTypedSchema(
    object({
        facilityId: string().required(t('signInFlow.facilityRequired')),
        email: string()
            .email(t('customerInformation.invalidEmailWarning'))
            .required(t('emailRequired')),
        firstName: string()
            .max(255)
            .min(1, t('firstNameRequired'))
            .required(t('firstNameRequired')),
        lastName: string()
            .max(255)
            .min(2, t('customerInformation.twoOrMoreCharactersRequired'))
            .required(t('customerInformation.twoOrMoreCharactersRequired')),
        subject: string().max(255).required(t('subjectRequired')),
        message: string().max(1000).required(t('messageRequired')),
        recaptchaToken: string().required(t('captchaRequired'))
    })
)

const { errors, defineField, defineComponentBinds, handleSubmit } = useForm({
    validationSchema: schema
})

const [emailValue, emailProps] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

const [firstNameValue, firstNameProps] = defineField('firstName', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [lastNameValue, lastNameProps] = defineField('lastName', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [subjectValue, subjectProps] = defineField('subject', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [messageValue, messageProps] = defineField('message', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const recaptchaValue = defineComponentBinds('recaptchaToken', {
    validateOnBlur: false
})

const [facilityId] = defineField('facilityId')
// This is for handling the 1 facility case
watch(activeFacilities, newValue => {
    if (newValue === null) {
        return
    }

    if (newValue.length === 1) {
        facilityId.value = newValue[0].id
    }
})

// This is for handling the case where a facility has been provided
onMounted(() => {
    if (props.facilityIdContext !== undefined) {
        facilityId.value = props.facilityIdContext
    }
})

const toast = useToast()
const { mutate, isPending } = useCreateAnonymousTicketMutation()
const onSubmit = handleSubmit(values => {
    mutate(
        {
            ...values
        },
        {
            onSuccess: data => {
                emit('ticketCreated', data.record)
                toast.add({
                    severity: 'success',
                    summary: t('success'),
                    detail: t('contactUsTicketCreated'),
                    life: 3000,
                    closable: false
                })
            }
        }
    )
})

function handleRecaptchaToken(token: string) {
    recaptchaValue.value['onUpdate:modelValue'](token)
    recaptchaValue.value.onBlur()
}
</script>
